<template>
  <transition name="coordinate-panel">
    <div v-if="value" class="coordinate-panel-shell">
      <div class="coordinate-panel-backdrop" @click="closePanel" />

      <aside class="coordinate-panel">
        <div class="coordinate-panel__header">
          <div class="coordinate-panel__title">
            <span class="coordinate-panel__icon">
              <i class="fas fa-calendar-week" />
            </span>
            <span>{{ panelTitle }}</span>
          </div>

          <div class="coordinate-panel__header-actions">
            <button
              type="button"
              class="coordinate-panel__icon-button coordinate-panel__icon-button--danger"
              @click="closePanel"
            >
              <i class="fal fa-times-circle" />
            </button>
          </div>
        </div>

        <div class="coordinate-panel__body">
          <custom-tab
            class="coordinate-panel__tabs"
            nav-class="coordinate-panel__tabs-nav"
          >
            <custom-tab-item title="Coordinación">
              <form
                class="coordinate-panel__student-search"
                :class="{
                  'coordinate-panel__section--disabled': isFormReadOnly,
                }"
                @submit.prevent="searchStudents"
              >
                <div class="coordinate-panel__student-search-row">
                  <div class="coordinate-panel__student-input-container">
                    <custom-input
                      v-model="studentSearch"
                      label="Número de SIE:"
                      placeholder="Escriba Número de SIE o Nombre de Estudiante"
                      @input="handleStudentSearchChange"
                    />
                    <button
                      v-if="hasSelectedStudent"
                      type="button"
                      class="coordinate-panel__student-clear"
                      aria-label="Quitar estudiante seleccionado"
                      @mousedown.prevent
                      @click="clearStudentSelection"
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>
                  <action-button
                    type="submit"
                    class="coordinate-panel__student-search-button"
                    text="Buscar"
                    i="fas fa-search"
                    variant="success"
                    :disabled="isStudentSearchLoading"
                  />
                </div>
                <div
                  v-if="studentSearchResults.length"
                  class="coordinate-panel__student-results"
                >
                  <button
                    v-for="studentResult in studentSearchResults"
                    :key="studentResult.studentSieId"
                    type="button"
                    class="coordinate-panel__student-result"
                    @click="handleStudentSubmit(studentResult)"
                  >
                    <span class="coordinate-panel__student-result-name">
                      {{ studentResult.fullName }}
                    </span>
                    <span class="coordinate-panel__student-result-sie">
                      SIE / {{ studentResult.studentSieId }}
                    </span>
                  </button>
                </div>
                <Error
                  v-if="studentSearchError"
                  variant="no-border"
                  :message="studentSearchError"
                />
              </form>

              <div
                v-if="hasSelectedStudent && lastCitationNumberDisplay"
                class="coordinate-panel__summary"
              >
                <span>Citaciones Totales</span>
                <span>{{ lastCitationNumberDisplay }}/5</span>
              </div>

              <div class="coordinate-panel__form">
                <ValidationObserver ref="formvalid" v-slot="{}">
                  <fieldset
                    class="coordinate-panel__fieldset"
                    :disabled="!hasSelectedStudent || isFormReadOnly"
                  >
                    <ValidationProvider
                      :key="'participant-' + participantValidationValue"
                      v-slot="participantValidation"
                      name="Participantes"
                      rules="required"
                      :custom-messages="selectionRequiredMessages"
                    >
                      <div>
                        <input
                          type="hidden"
                          :value="participantValidationValue"
                        />
                        <div class="d-flex flex-nowrap align-items-center mb-2">
                          <check-box
                            v-model="model.parents"
                            class="font-weight-light text-dark mr-3 w-auto d-inline-flex"
                            text="Padres"
                          />
                          <check-box
                            v-model="model.student"
                            class="font-weight-light text-dark w-auto d-inline-flex"
                            text="Estudiante"
                          />
                        </div>
                        <Error
                          v-if="participantValidation.errors[0]"
                          variant="no-border"
                          :message="participantValidation.errors[0]"
                        />
                      </div>
                    </ValidationProvider>

                    <ValidationProvider
                      v-slot="dateValidation"
                      name="Fecha"
                      rules="required"
                    >
                      <custom-date-picker
                        v-model="model.date"
                        label="Fecha:"
                        format="dd/MM/yyyy"
                        :min-date="minAppointmentDate"
                        placeholder="DD/MM/YYYY"
                        :error="dateValidation.errors[0]"
                        :custom-error-msg="dateValidation.errors[0]"
                      />
                    </ValidationProvider>

                    <div
                      class="coordinate-panel__grid coordinate-panel__grid--two"
                    >
                      <ValidationProvider
                        v-slot="startTimeValidation"
                        name="Desde"
                        rules="required"
                      >
                        <custom-time-input
                          v-model="model.startTime"
                          :period-value.sync="model.startAmPm"
                          label="Desde:"
                          placeholder="hh:mm"
                          twelve-hour-clock
                          :error="startTimeValidation.errors[0]"
                          :custom-error-msg="startTimeValidation.errors[0]"
                        />
                      </ValidationProvider>
                      <ValidationProvider
                        v-slot="endTimeValidation"
                        name="Hasta"
                        :rules="endTimeRules"
                        :custom-messages="endTimeMessages"
                      >
                        <custom-time-input
                          v-model="model.endTime"
                          :period-value.sync="model.endAmPm"
                          label="Hasta:"
                          placeholder="hh:mm"
                          twelve-hour-clock
                          :error="endTimeValidation.errors[0]"
                          :custom-error-msg="endTimeValidation.errors[0]"
                        />
                      </ValidationProvider>
                    </div>

                    <ValidationProvider
                      v-slot="placeValidation"
                      name="Lugar"
                      rules="required"
                    >
                      <custom-input
                        v-model="model.place"
                        label="Lugar:"
                        :error="placeValidation.errors[0]"
                      />
                    </ValidationProvider>

                    <ValidationProvider
                      v-if="hasSelectedStudent"
                      v-slot="citationNumberValidation"
                      name="Número de Citación"
                      rules="required"
                      :custom-messages="citationNumberMessages"
                    >
                      <custom-drop-down
                        v-model="model.citationNumber"
                        :options="citationNumberOptions"
                        label="Número de Citación:"
                        fieldkey="id"
                        fieldname="name"
                        placeholder="Seleccione"
                        :error="citationNumberValidation.errors[0]"
                        :custom-error-msg="citationNumberValidation.errors[0]"
                        @input="handleCitationNumberSelected"
                      />
                    </ValidationProvider>

                    <div class="coordinate-panel__purpose-group">
                      <p class="coordinate-panel__label">Propósito:</p>
                      <div
                        v-if="!purposeOptionsReadOnly"
                        class="coordinate-panel__purpose-row"
                      >
                        <custom-drop-down
                          v-model="model.purpose"
                          :options="availablePurposeOptions"
                          fieldkey="id"
                          fieldname="name"
                          placeholder="Seleccione"
                        />
                        <action-button
                          class="coordinate-panel__add-button"
                          i="fas fa-file-plus"
                          text="Añadir"
                          variant="success"
                          @click="addPurpose"
                        />
                      </div>
                    </div>

                    <ValidationProvider
                      v-if="isOtherPurposeSelected"
                      v-slot="otherPurposeValidation"
                      name="Escriba Otro"
                      rules="required"
                    >
                      <custom-input
                        v-model="model.otherPurpose"
                        label="Escriba Otro:"
                        :error="
                          purposeOtherError || otherPurposeValidation.errors[0]
                        "
                        custom-error-msg="Campo obligatorio"
                      />
                    </ValidationProvider>

                    <ValidationProvider
                      :key="'purpose-' + selectedPurposesValidationValue"
                      v-slot="purposeValidation"
                      name="Propósito"
                      rules="required"
                    >
                      <div
                        class="coordinate-panel__selected"
                        :class="{
                          'coordinate-panel__selected--readonly': purposeOptionsReadOnly,
                        }"
                      >
                        <input
                          type="hidden"
                          :value="selectedPurposesValidationValue"
                        />
                        <custom-chips-group
                          :selected-items="selectedPurposes"
                          :title="
                            purposeOptionsReadOnly ? '' : 'Seleccionado(s):'
                          "
                          object-text="name"
                          :no-background="true"
                          :disabled="purposeOptionsReadOnly"
                          :on-item-deleted="handlePurposeDeletion"
                          @itemMarkedForDeletion="handlePurposeDeletion"
                        />
                        <Error
                          v-if="purposeValidation.errors[0]"
                          variant="no-border"
                          message="Debe seleccionar al menos un propósito de reunión."
                        />
                      </div>
                    </ValidationProvider>

                    <warning-box
                      v-if="hasPurposeReminderWarning"
                      class="coordinate-panel__purpose-warning mx-0"
                      title=""
                    >
                      <div
                        v-for="message in purposeReminderMessages"
                        :key="message"
                      >
                        {{ message }}
                      </div>
                    </warning-box>

                    <fieldset
                      class="coordinate-panel__fieldset coordinate-panel__fieldset--nested"
                      :disabled="hasActivePurposeReminder"
                    >
                      <ValidationProvider
                        v-if="
                          hasEvaluationDiscussionPurpose &&
                            (!isFormReadOnly || selectedEvaluations.length)
                        "
                        :key="
                          'pending-evaluations-' +
                            selectedEvaluationsValidationValue
                        "
                        v-slot="evaluationValidation"
                        name="Evaluaciones"
                        rules="required"
                      >
                        <div class="coordinate-panel__evaluations">
                          <input
                            type="hidden"
                            :value="selectedEvaluationsValidationValue"
                          />

                          <p
                            v-if="!isFormReadOnly"
                            class="coordinate-panel__label"
                          >
                            Evaluaciones Pendientes:
                          </p>
                          <div
                            v-if="!isFormReadOnly"
                            class="coordinate-panel__evaluation-row"
                          >
                            <v-select
                              v-model="selectedPendingEvaluation"
                              class="coordinate-panel__evaluation-select"
                              :options="availablePendingEvaluations"
                              label="name"
                              placeholder="Seleccione"
                            >
                              <template #option="evaluation">
                                <div
                                  class="coordinate-panel__evaluation-option"
                                >
                                  <span
                                    class="coordinate-panel__evaluation-option-name"
                                  >
                                    {{ evaluation.name }}
                                  </span>
                                  <span
                                    class="coordinate-panel__evaluation-option-date"
                                  >
                                    {{
                                      formatEvaluationDate(
                                        evaluation.evaluationDate
                                      )
                                    }}
                                  </span>
                                </div>
                              </template>
                              <template #selected-option="evaluation">
                                <span>{{ evaluation.name }}</span>
                              </template>
                            </v-select>
                            <action-button
                              class="coordinate-panel__add-button"
                              i="fas fa-file-plus"
                              text="Añadir"
                              variant="success"
                              @click="addPendingEvaluation"
                            />
                          </div>

                          <div
                            v-if="
                              !isFormReadOnly &&
                                !isPendingEvaluationsLoading &&
                                !pendingEvaluations.length
                            "
                            class="coordinate-panel__empty-message"
                          >
                            No hay evaluaciones pendientes disponibles para este
                            estudiante.
                          </div>

                          <div
                            v-if="selectedEvaluations.length"
                            class="coordinate-panel__evaluation-list"
                          >
                            <p class="coordinate-panel__label mt-2">
                              Evaluacion(es) Seleccionada(s):
                            </p>
                            <div
                              v-for="evaluation in selectedEvaluations"
                              :key="evaluation.studentEvaluationId"
                              class="coordinate-panel__evaluation-row-item"
                            >
                              <div class="coordinate-panel__evaluation-info">
                                <div>
                                  <span class="font-weight-bold">
                                    Nombre de Evaluación:
                                  </span>
                                  {{ evaluation.name }}
                                </div>
                                <div>
                                  <span class="font-weight-bold">
                                    Fecha de Evaluación:
                                  </span>
                                  {{
                                    formatEvaluationDate(
                                      evaluation.evaluationDate
                                    )
                                  }}
                                </div>
                              </div>
                              <button
                                v-if="!isFormReadOnly"
                                type="button"
                                class="coordinate-panel__participant-remove"
                                @click="
                                  removePendingEvaluation(
                                    evaluation.studentEvaluationId
                                  )
                                "
                              >
                                <i class="fal fa-times-circle" />
                              </button>
                            </div>
                          </div>

                          <Error
                            v-if="
                              !isFormReadOnly && evaluationValidation.errors[0]
                            "
                            variant="no-border"
                            message="Debe seleccionar al menos una evaluación para discutir."
                          />
                        </div>
                      </ValidationProvider>

                      <div
                        v-if="canShowAppointmentParticipants"
                        class="coordinate-panel__section-title"
                      >
                        <span class="coordinate-panel__section-icon">
                          <i class="fas fa-user-circle" />
                        </span>
                        <span class="font-weight-bold">
                          Información Participantes
                        </span>
                      </div>

                      <ValidationProvider
                        v-if="canShowAppointmentParticipants"
                        :key="
                          'appointment-participants-' +
                            appointmentParticipantsValidationValue
                        "
                        v-slot="appointmentParticipantValidation"
                        name="Participantes de la cita"
                        rules="required"
                        :custom-messages="appointmentParticipantMessages"
                      >
                        <div class="coordinate-panel__participants">
                          <input
                            type="hidden"
                            :value="appointmentParticipantsValidationValue"
                          />
                          <div
                            v-for="participant in regularParticipantOptions"
                            :key="participant.id"
                            class="coordinate-panel__participant-option"
                          >
                            <check-box
                              :key="
                                `participant-${participant.id}-${appointmentParticipantRenderKey}`
                              "
                              :value="
                                isAppointmentParticipantSelected(participant.id)
                              "
                              class="font-weight-light text-dark mb-1"
                              :text="participant.name"
                              @input="
                                handleAppointmentParticipantToggle(
                                  participant,
                                  $event
                                )
                              "
                            />
                          </div>

                          <div
                            v-if="otherParticipantOption"
                            class="coordinate-panel__participant-other"
                          >
                            <check-box
                              :key="
                                `other-participant-${appointmentParticipantRenderKey}`
                              "
                              :value="isOtherAppointmentParticipantChecked"
                              class="font-weight-light text-dark mb-1 w-auto d-inline-flex"
                              text="Otro:"
                              @input="
                                handleAppointmentParticipantToggle(
                                  otherParticipantOption,
                                  $event
                                )
                              "
                            />
                            <custom-input
                              v-if="shouldShowOtherParticipantInput"
                              v-model="appointmentOtherParticipantName"
                              class="coordinate-panel__participant-other-input"
                              maxlength="100"
                              :error="appointmentOtherParticipantError"
                            />
                            <action-button
                              v-if="shouldShowOtherParticipantInput"
                              class="coordinate-panel__add-button"
                              i="fas fa-file-plus"
                              text="Añadir"
                              variant="success"
                              @click="addAppointmentOtherParticipant"
                            />
                          </div>

                          <Error
                            v-if="appointmentParticipantValidation.errors[0]"
                            variant="no-border"
                            :message="
                              appointmentParticipantValidation.errors[0]
                            "
                          />

                          <div
                            v-if="appointmentOtherParticipants.length"
                            class="coordinate-panel__participant-other-list"
                          >
                            <div
                              v-for="(participant,
                              index) in appointmentOtherParticipants"
                              :key="`${participant}-${index}`"
                              class="coordinate-panel__participant-other-row"
                            >
                              <span>{{ participant }}</span>
                              <button
                                type="button"
                                class="coordinate-panel__participant-remove"
                                @click="
                                  removeAppointmentOtherParticipant(index)
                                "
                              >
                                <i class="fal fa-times-circle" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </ValidationProvider>

                      <div class="coordinate-panel__section-title">
                        <span class="coordinate-panel__section-icon">
                          <i class="fas fa-user-circle" />
                        </span>
                        <span class="font-weight-bold">Funcionario</span>
                      </div>

                      <div
                        class="coordinate-panel__grid coordinate-panel__grid--name"
                      >
                        <ValidationProvider
                          v-slot="firstNameValidation"
                          name="Nombre"
                          rules="required"
                        >
                          <custom-input
                            v-model="model.firstName"
                            label="Nombre:"
                            :error="firstNameValidation.errors[0]"
                          />
                        </ValidationProvider>
                        <custom-input
                          v-model="model.initial"
                          label="Inicial:"
                        />
                      </div>
                      <div
                        class="coordinate-panel__grid coordinate-panel__grid--two"
                      >
                        <ValidationProvider
                          v-slot="lastNameValidation"
                          name="Apellido Paterno"
                          rules="required"
                        >
                          <custom-input
                            v-model="model.lastName"
                            label="Apellido Paterno:"
                            :error="lastNameValidation.errors[0]"
                          />
                        </ValidationProvider>
                        <ValidationProvider
                          v-slot="secondLastNameValidation"
                          name="Apellido Materno"
                          rules="required"
                        >
                          <custom-input
                            v-model="model.secondLastName"
                            label="Apellido Materno:"
                            :error="secondLastNameValidation.errors[0]"
                          />
                        </ValidationProvider>
                      </div>
                      <ValidationProvider
                        v-slot="emailValidation"
                        name="Correo Electrónico"
                        rules="required|email"
                        :custom-messages="emailMessages"
                      >
                        <custom-input
                          v-model="model.email"
                          type="email"
                          label="Correo Electrónico:"
                          :error="emailValidation.errors[0]"
                        />
                      </ValidationProvider>
                      <ValidationProvider
                        v-slot="phoneValidation"
                        name="Teléfono"
                        rules="required"
                      >
                        <custom-input
                          v-model="model.phone"
                          placeholder="(__) __-____"
                          mask-type="Phone"
                          label="Teléfono:"
                          :error="phoneValidation.errors[0]"
                        />
                      </ValidationProvider>

                      <ValidationProvider
                        :key="'notification-' + notificationValidationValue"
                        v-slot="notificationValidation"
                        name="Notificación"
                        rules="required"
                        :custom-messages="selectionRequiredMessages"
                      >
                        <div>
                          <input
                            type="hidden"
                            :value="notificationValidationValue"
                          />
                          <label class="coordinate-panel__label">
                            Se notifica a los Padres a través de:
                          </label>
                          <div class="d-flex flex-column">
                            <check-box
                              v-model="model.notifyPersonal"
                              class="font-weight-light text-dark mb-1"
                              text="Personal"
                            />
                            <check-box
                              v-model="model.notifyPhone"
                              class="font-weight-light text-dark mb-1"
                              text="Vía Teléfono"
                            />
                            <check-box
                              v-model="model.notifyEmail"
                              class="font-weight-light text-dark mb-1"
                              text="Correo Electrónico"
                            />
                            <div class="coordinate-panel__notification-other">
                              <check-box
                                v-model="model.notifyOther"
                                class="font-weight-light text-dark mb-1 w-auto d-inline-flex"
                                text="Otro"
                              />
                              <ValidationProvider
                                v-slot="otherNotificationValidation"
                                name="Otro"
                                rules="required"
                                v-if="model.notifyOther"
                              >
                                <custom-input
                                  v-model="model.otherNotification"
                                  class="coordinate-panel__notification-other-input"
                                  :error="otherNotificationValidation.errors[0]"
                                />
                              </ValidationProvider>
                            </div>
                          </div>
                          <Error
                            v-if="notificationValidation.errors[0]"
                            variant="no-border"
                            :message="notificationValidation.errors[0]"
                          />
                        </div>
                      </ValidationProvider>
                    </fieldset>
                  </fieldset>
                </ValidationObserver>
                <div
                  class="coordinate-panel__sign-plan"
                  :class="{
                    'coordinate-panel__sign-plan--disabled':
                      !hasSelectedStudent ||
                      isFormReadOnly ||
                      hasActivePurposeReminder,
                  }"
                >
                  <sign-plan
                    :is-electronic-signature.sync="model.isElectronicSignature"
                    question-label="¿Cuál será el mecanismo que se estará utilizando para firmar la Citación?"
                    @change="handleSelectMechanism"
                  />
                  <Error
                    v-if="mechanismError"
                    variant="no-border"
                    :message="mechanismError"
                  />
                </div>
                <div v-if="hasSelectMechanismElectronic" class="mt-3">
                  <signature-hub-info
                    :signature-info="signatureHubInfo"
                    @configure-sign="handleConfigureSignatures"
                  />
                  <Error
                    v-if="hasCancellationReason"
                    variant="pl-3"
                    :message="model.digitalSignatureDocumentCancellationReason"
                  />
                </div>

                <div
                  v-if="hasSelectMechanisManuscript && !isFormReadOnly"
                  class="coordinate-panel__manuscript-section"
                >
                  <div class="coordinate-panel__section-title mt-3 mb-2">
                    <span class="coordinate-panel__section-icon">
                      <i class="fas fa-file-signature" />
                    </span>
                    <span class="font-weight-bold">
                      Documento de Firma Manuscrita
                    </span>
                  </div>
                  <div
                    class="coordinate-panel__manuscript-doc bg-light rounded px-3 py-3"
                  >
                    <p class="color-gray-6 font-14 mb-1">
                      Tipo de Documento:
                    </p>
                    <p class="font-weight-bold mb-2">Citación</p>
                    <custom-file-input
                      id="manuscriptDoc"
                      v-if="manuscriptDocumentUploaded && manuscriptDocument"
                      :key="`uploaded-${manuscriptFileInputKey}`"
                      :attachment-id="String(manuscriptDocument.documentId)"
                      class="w-100"
                      display-style="normal"
                      label=""
                      :name="manuscriptDocument.documentFilename"
                      :on-download="downloadManuscriptDocument"
                      @after-remove="onRemoveManuscript"
                    />
                    <action-button
                      v-else
                      id="btnDownloadManuscript"
                      class="w-100"
                      i="fas fa-file-download text-white mr-n1 pr-2"
                      text="Descargar Citación"
                      variant="success"
                      @click="downloadManuscriptTemplate"
                    />
                  </div>
                </div>

                <div class="d-flex justify-content-end align-items-center mt-4">
                  <action-button
                    id="cancel-coordinate-appointment"
                    class="mr-2"
                    i="fas fa-times-circle"
                    text="Cancelar"
                    variant="outline-danger"
                    @click="closePanel"
                  />
                  <action-button
                    v-if="canSignDocument"
                    i="fas fa-signature"
                    text="Firmar Documento"
                    variant="success ml-2"
                    @click="handleSignDocument"
                  />
                  <action-button
                    v-if="!isFormReadOnly"
                    i="fas fa-chevron-circle-right"
                    :text="continueButtonText"
                    variant="success ml-2"
                    :disabled="isContinueDisabled"
                    :loading="isSaving"
                    @click="continueAppointment()"
                  />
                </div>
              </div>
            </custom-tab-item>
          </custom-tab>
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
import ActionButton from 'ActionButton';
import CheckBox from 'CheckBox';
import CustomFileInput from 'CustomFileInput';
import objectToFormData from 'object-to-formdata';
import CustomChipsGroup from 'CustomChipsGroup';
import CustomDatePicker from 'custom-date-picker';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import CustomTimeInput from '@/components/common/CustomTimeInput';
import Error from '@/components/common/Error';
import dateUtil from '@/utils/dateUtil';
import mixAlert from '@/mixin/alert.mixin';
import { CustomTab, CustomTabItem } from 'custom-tabs';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';
import compuAppointmentService from '@/modules/compu-appointment/api';
import moment from 'moment';
import { SignPlan } from '@/components/sign-plan';
import { SignatureHubInfo } from '@quisit/signature-hub';
import SignatureHubTypes from '@/utils/constants/signatureHubTypes';
import compuAppointmentStatus from '@/utils/constants/compuAppointmentStatus';
import { getSignatureHubInfo as _getSignatureHubInfo } from '@/services/signatureHub/api';
import reportService from '@/services/report';
import DigitalSignatureDocumentStatus from '@/utils/constants/digitalSignatureDocumentStatus';
import WarningBox from '@/components/pei/service/components/ServiceWarningBox';

const OTHER_PURPOSE_ID = 5;
const OTHER_APPOINTMENT_PARTICIPANT_TYPE_ID = 6;
const _MAX_OTHER_APPOINTMENT_PARTICIPANTS = 6;
const _COMPU_APPOINTMENT_STATUS_COORDINADA = 2;

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const getTimeInMinutes = (time, period) => {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) {
    return null;
  }

  const [hourText, minuteText] = time.split(':');
  let hours = Number(hourText);
  const minutes = Number(minuteText);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  if (period === 'PM' && hours < 12) {
    hours += 12;
  }

  if (period === 'AM' && hours === 12) {
    hours = 0;
  }

  return hours * 60 + minutes;
};

extend('endTimeAfterStart', {
  params: ['startTime', 'startPeriod', 'endPeriod'],
  validate(endTime, { startTime, startPeriod, endPeriod }) {
    const startMinutes = getTimeInMinutes(startTime, startPeriod);
    const endMinutes = getTimeInMinutes(endTime, endPeriod);

    if (startMinutes === null || endMinutes === null) {
      return true;
    }

    return endMinutes > startMinutes;
  },
});

const getDefaultModel = () => ({
  studentSieId: null,
  parents: false,
  student: false,
  date: null,
  startTime: null,
  startAmPm: 'AM',
  endTime: null,
  endAmPm: 'AM',
  place: '',
  purpose: null,
  otherPurpose: '',
  firstName: '',
  initial: '',
  lastName: '',
  secondLastName: '',
  email: '',
  phone: '',
  notifyPersonal: false,
  notifyPhone: false,
  notifyEmail: false,
  notifyOther: false,
  otherNotification: '',
  isElectronicSignature: null,
  citationNumber: null,
});

const getFirstAvailableValue = (source, fields) =>
  fields.map((field) => (source ? source[field] : null)).find(Boolean) || '';

export default {
  name: 'CoordinateAppointmentPanel',
  mixins: [mixAlert],
  components: {
    ActionButton,
    CheckBox,
    CustomChipsGroup,
    CustomDatePicker,
    CustomDropDown,
    CustomInput,
    CustomTab,
    CustomTabItem,
    CustomTimeInput,
    Error,
    ValidationObserver,
    ValidationProvider,
    SignPlan,
    SignatureHubInfo,
    CustomFileInput,
    WarningBox,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    appointmentId: {
      type: Number,
      default: null,
    },
    isRecoordination: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedPurposes: [],
      appointmentParticipantOptions: [],
      selectedAppointmentParticipantTypeIds: [],
      appointmentOtherParticipantName: '',
      appointmentOtherParticipantError: '',
      appointmentOtherParticipants: [],
      appointmentParticipantRenderKey: 0,
      model: getDefaultModel(),
      purposeOptions: [],
      purposeOptionsReadOnly: false,
      purposeOtherError: '',
      pendingEvaluations: [],
      selectedPendingEvaluation: null,
      selectedEvaluations: [],
      isPendingEvaluationsLoading: false,
      activePurposeReminder: null,
      selectedStudent: null,
      appointmentHistory: [],
      isAppointmentHistoryLoading: false,
      studentSearch: '',
      studentSearchError: '',
      studentSearchResults: [],
      isStudentSearchLoading: false,
      signatureHubInfo: null,
      mechanismError: '',
      isSaving: false,
      currentAppointmentId: null,
      appointmentStatusCode: null,
      hasDownloadedManuscriptTemplate: false,
      manuscriptFileInput: null,
      manuscriptFileInputKey: 0,
      manuscriptTemplateSnapshot: null,
      manuscriptDocumentUploaded: false,
      manuscriptDocument: null,
    };
  },
  computed: {
    panelTitle() {
      return this.isRecoordination ? 'Recoordinar Cita' : 'Coordinar Cita';
    },
    hasSelectedStudent() {
      return Boolean(this.selectedStudent);
    },
    lastCitationNumberDisplay() {
      const lastCitationNumber = Number(
        this.selectedStudent?.lastCitationNumber
      );

      return lastCitationNumber > 0 ? lastCitationNumber : null;
    },
    selectionRequiredMessages() {
      return {
        required: 'Seleccione al menos 1 opción',
      };
    },
    citationNumberMessages() {
      return {
        required: 'Debe seleccionar el número de citación para continuar.',
      };
    },
    citationNumberOptions() {
      return [1, 2, 3, 4, 5].map((citationNumber) => ({
        id: citationNumber,
        name: `Cita ${citationNumber}`,
      }));
    },
    canShowAppointmentParticipants() {
      return (
        this.hasSelectedStudent &&
        (this.currentAppointmentId ||
          Boolean(this.getCitationNumberValue(this.model.citationNumber)))
      );
    },
    appointmentParticipantMessages() {
      return {
        required: 'Debe seleccionar al menos un participante.',
      };
    },
    regularParticipantOptions() {
      return this.appointmentParticipantOptions.filter(
        (participant) => !participant.isOther
      );
    },
    otherParticipantOption() {
      return this.appointmentParticipantOptions.find(
        (participant) => participant.isOther
      );
    },
    isOtherAppointmentParticipantChecked() {
      return this.selectedAppointmentParticipantTypeIds.includes(
        OTHER_APPOINTMENT_PARTICIPANT_TYPE_ID
      );
    },
    canAddAppointmentOtherParticipants() {
      return (
        this.appointmentOtherParticipants.length <
        _MAX_OTHER_APPOINTMENT_PARTICIPANTS
      );
    },
    shouldShowOtherParticipantInput() {
      return (
        this.isOtherAppointmentParticipantChecked &&
        this.canAddAppointmentOtherParticipants
      );
    },
    appointmentParticipantsValidationValue() {
      const hasRegularParticipant = this.selectedAppointmentParticipantTypeIds.filter(
        (id) => id !== OTHER_APPOINTMENT_PARTICIPANT_TYPE_ID
      ).length;

      return hasRegularParticipant || this.appointmentOtherParticipants.length
        ? 'selected'
        : '';
    },
    endTimeMessages() {
      return {
        endTimeAfterStart: 'Hasta debe ser mayor que Desde',
      };
    },
    endTimeRules() {
      return {
        required: true,
        endTimeAfterStart: {
          startTime: this.model.startTime,
          startPeriod: this.model.startAmPm,
          endPeriod: this.model.endAmPm,
        },
      };
    },
    emailMessages() {
      return {
        email: 'Favor de entrar un correo electrónico válido.',
      };
    },
    availablePurposeOptions() {
      const selectedPurposeIds = this.selectedPurposes.map(
        (purpose) => purpose.id
      );

      return this.purposeOptions.filter(
        (purpose) => !selectedPurposeIds.includes(purpose.id)
      );
    },
    selectedPurposeIds() {
      return this.selectedPurposes.map((purpose) => purpose.id);
    },
    isOtherPurposeSelected() {
      return this.model.purpose && this.model.purpose.id === OTHER_PURPOSE_ID;
    },
    hasEvaluationDiscussionPurpose() {
      return this.selectedPurposes.some((purpose) => {
        const purposeName = normalizeText(purpose.name);

        return (
          purposeName.includes('discutir') && purposeName.includes('evaluacion')
        );
      });
    },
    hasActivePurposeReminder() {
      return Boolean(this.activePurposeReminder?.hasActiveAppointment);
    },
    hasRejectedPurposeReminder() {
      return Boolean(
        this.activePurposeReminder?.hasRejectedAppointmentReminder
      );
    },
    hasPurposeReminderWarning() {
      return this.hasActivePurposeReminder || this.hasRejectedPurposeReminder;
    },
    activePurposeReminderMessage() {
      if (!this.hasActivePurposeReminder) {
        return '';
      }

      const purposeDescription =
        this.activePurposeReminder.purposeDescription || 'seleccionado';

      return `Ya existe una cita coordinada para este propósito(s) ${purposeDescription}. Se recomienda dar seguimiento a la cita(s) existente antes de crear una nueva.`;
    },
    rejectedPurposeReminderMessage() {
      if (!this.hasRejectedPurposeReminder) {
        return '';
      }

      const purposeName =
        this.activePurposeReminder.rejectedPurposeName || 'seleccionado';
      const count = Number(
        this.activePurposeReminder.rejectedAppointmentCount || 0
      );

      if (count === 1) {
        return `El propósito '${purposeName}' ya tiene una cita previa rechazada. Esta sería la 2da cita.`;
      }

      if (count === 2) {
        return `El propósito '${purposeName}' ya tiene dos citas previas rechazadas. Esta sería la 3ra cita.`;
      }

      return `El propósito '${purposeName}' ya tiene ${count} citas previas rechazadas. Verifique la documentación requerida para una 4ta cita.`;
    },
    purposeReminderMessages() {
      return [
        this.activePurposeReminderMessage,
        this.rejectedPurposeReminderMessage,
      ].filter(Boolean);
    },
    availablePendingEvaluations() {
      const selectedEvaluationIds = this.selectedEvaluations.map(
        (evaluation) => evaluation.studentEvaluationId
      );

      return this.pendingEvaluations.filter(
        (evaluation) =>
          !selectedEvaluationIds.includes(evaluation.studentEvaluationId)
      );
    },
    selectedEvaluationsValidationValue() {
      if (!this.hasEvaluationDiscussionPurpose) {
        return 'selected';
      }

      return this.selectedEvaluations.length ? 'selected' : '';
    },
    selectedPurposesValidationValue() {
      return this.selectedPurposes.length ? 'selected' : '';
    },
    participantValidationValue() {
      return this.model.parents || this.model.student ? 'selected' : '';
    },
    notificationValidationValue() {
      return this.model.notifyPersonal ||
        this.model.notifyPhone ||
        this.model.notifyEmail ||
        this.model.notifyOther
        ? 'selected'
        : '';
    },
    minAppointmentDate() {
      const today = new Date();

      return new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 1
      );
    },
    hasSelectMechanisManuscript() {
      return this.model.isElectronicSignature === false;
    },
    hasSelectMechanismElectronic() {
      return this.model.isElectronicSignature === true;
    },
    requiresManuscriptTemplateDownload() {
      return (
        this.hasSelectMechanisManuscript &&
        !this.manuscriptDocumentUploaded &&
        !this.hasDownloadedManuscriptTemplate
      );
    },
    continueButtonText() {
      return this.hasSelectMechanisManuscript &&
        this.hasDownloadedManuscriptTemplate
        ? 'Coordinar'
        : 'Continuar';
    },
    isContinueDisabled() {
      return (
        this.isSaving ||
        this.hasActivePurposeReminder ||
        this.requiresManuscriptTemplateDownload
      );
    },
    isCoordinated() {
      return (
        this.appointmentStatusCode === _COMPU_APPOINTMENT_STATUS_COORDINADA
      );
    },
    isHubDocumentSent() {
      const statusId = this.signatureHubInfo?.documentInfo?.documentStatusId;
      return (
        statusId != null &&
        statusId !== DigitalSignatureDocumentStatus.DocNotGenerated
      );
    },
    isFormReadOnly() {
      return (
        !this.isRecoordination && (this.isCoordinated || this.isHubDocumentSent)
      );
    },
    canSignDocument() {
      const noValidStatusesForSigning = [
        DigitalSignatureDocumentStatus.Signed,
        DigitalSignatureDocumentStatus.Cancelled,
        DigitalSignatureDocumentStatus.RenderedIneffective,
        DigitalSignatureDocumentStatus.Rejected,
      ];
      return (
        this.hasSelectMechanismElectronic &&
        this.isHubDocumentSent &&
        !noValidStatusesForSigning.includes(
          this.signatureHubInfo?.documentInfo?.documentStatusId
        )
      );
    },
    manuscriptTemplateSignature() {
      return this.getManuscriptTemplateSnapshot();
    },
    hasCancellationReason() {
      return Boolean(this.model.digitalSignatureDocumentCancellationReason);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.resetPanel();
          if (this.appointmentId) {
            this.loadAppointment(this.appointmentId);
          }
        }
      },
    },
    'model.purpose'() {
      this.purposeOtherError = '';

      if (!this.isOtherPurposeSelected) {
        this.model.otherPurpose = '';
      }
    },
    'model.otherPurpose'() {
      if (this.model.otherPurpose) {
        this.purposeOtherError = '';
      }
    },
    appointmentOtherParticipantName() {
      if (this.appointmentOtherParticipantName) {
        this.appointmentOtherParticipantError = '';
      }
    },
    'model.notifyOther'(isSelected) {
      if (!isSelected) {
        this.model.otherNotification = '';
      }
    },
    manuscriptTemplateSignature(value) {
      this.invalidateManuscriptDocumentIfOutdated(value);
    },
  },
  methods: {
    noop() {},
    async handlePurposeDeletion(purpose) {
      if (this.purposeOptionsReadOnly) {
        return;
      }

      await this.removePurpose(purpose);
    },
    async fetchPurposes() {
      try {
        this.purposeOptions = await compuAppointmentService.fetchPurposes();
      } catch {
        this.purposeOptions = [];
      }
    },
    async fetchPurposeOptions({ preserveSelection = false } = {}) {
      const citationNumber = this.getCitationNumberValue(
        this.model.citationNumber
      );

      if (!this.selectedStudent || !citationNumber) {
        this.purposeOptions = [];
        this.purposeOptionsReadOnly = false;
        return;
      }

      try {
        const {
          data: response,
        } = await compuAppointmentService.fetchPurposeOptions(
          this.selectedStudent.studentSieId,
          citationNumber
        );

        this.purposeOptions = Array.isArray(response?.options)
          ? response.options
          : [];
        this.purposeOptionsReadOnly = Boolean(response?.isReadOnly);
        if (preserveSelection) {
          await this.validateActivePurposeReminder();
        } else {
          this.applyPreselectedPurposes(response?.preselectedPurposeIds || []);
        }
      } catch {
        this.purposeOptions = [];
        this.purposeOptionsReadOnly = false;
      }
    },
    clearActivePurposeReminder() {
      this.activePurposeReminder = null;
    },
    async validateActivePurposeReminder() {
      this.clearActivePurposeReminder();

      if (!this.selectedStudent || !this.selectedPurposes.length) {
        return;
      }

      const purposeIds = this.selectedPurposes
        .map((purpose) => purpose?.id)
        .filter((purposeId) => purposeId && purposeId !== OTHER_PURPOSE_ID);

      if (!purposeIds.length) {
        return;
      }

      try {
        const {
          data: response,
        } = await compuAppointmentService.fetchPurposeReminders(
          this.selectedStudent.studentSieId,
          purposeIds,
          this.currentAppointmentId || null
        );

        this.activePurposeReminder = response || null;
      } catch {
        this.clearActivePurposeReminder();
      }
    },
    async fetchAppointmentParticipantOptions() {
      const citationNumber = this.getCitationNumberValue(
        this.model.citationNumber
      );

      if (!this.selectedStudent || !citationNumber) {
        this.appointmentParticipantOptions = [];
        return;
      }

      try {
        const {
          data: response,
        } = await compuAppointmentService.fetchParticipantOptions(
          this.selectedStudent.studentSieId,
          citationNumber
        );

        this.appointmentParticipantOptions = Array.isArray(response)
          ? response
          : [];
        this.normalizeSelectedAppointmentParticipants();
      } catch {
        this.appointmentParticipantOptions = [];
      }
    },
    async fetchPendingEvaluations(studentSieId) {
      if (!studentSieId) {
        this.pendingEvaluations = [];
        return;
      }

      this.isPendingEvaluationsLoading = true;

      try {
        const {
          data: response,
        } = await compuAppointmentService.fetchPendingEvaluations(studentSieId);

        this.pendingEvaluations = Array.isArray(response)
          ? response.map((evaluation) => ({
              ...evaluation,
              displayName: `${evaluation.name} - ${this.formatEvaluationDate(
                evaluation.evaluationDate
              )}`,
            }))
          : [];
      } catch {
        this.pendingEvaluations = [];
      } finally {
        this.isPendingEvaluationsLoading = false;
      }
    },
    handleCitationNumberSelected(citationNumber) {
      this.model.citationNumber = this.getCitationNumberOption(citationNumber);
      this.clearPurposes();
      this.clearAppointmentParticipants();
      this.fetchPurposeOptions();
      this.fetchAppointmentParticipantOptions();
    },
    clearPurposes() {
      this.selectedPurposes = [];
      this.model.purpose = null;
      this.model.otherPurpose = '';
      this.purposeOptions = [];
      this.purposeOptionsReadOnly = false;
      this.purposeOtherError = '';
      this.clearActivePurposeReminder();
      this.clearSelectedEvaluations();
    },
    applyPreselectedPurposes(preselectedPurposeIds) {
      const ids = Array.isArray(preselectedPurposeIds)
        ? preselectedPurposeIds
        : [];

      if (!ids.length) {
        return;
      }

      this.selectedPurposes = this.purposeOptions
        .filter((purpose) => ids.includes(purpose.id))
        .map((purpose) => ({
          ...purpose,
          disabled: this.purposeOptionsReadOnly,
        }));

      this.$nextTick(() => this.validateActivePurposeReminder());
    },
    clearAppointmentParticipants() {
      this.appointmentParticipantOptions = [];
      this.selectedAppointmentParticipantTypeIds = [];
      this.appointmentOtherParticipantName = '';
      this.appointmentOtherParticipantError = '';
      this.appointmentOtherParticipants = [];
      this.appointmentParticipantRenderKey++;
    },
    normalizeSelectedAppointmentParticipants() {
      const optionIds = this.appointmentParticipantOptions.map(
        (participant) => participant.id
      );

      this.selectedAppointmentParticipantTypeIds = this.selectedAppointmentParticipantTypeIds.filter(
        (id) => optionIds.includes(id)
      );

      if (!optionIds.includes(OTHER_APPOINTMENT_PARTICIPANT_TYPE_ID)) {
        this.appointmentOtherParticipantName = '';
        this.appointmentOtherParticipants = [];
      }
    },
    isAppointmentParticipantSelected(participantTypeId) {
      return this.selectedAppointmentParticipantTypeIds.includes(
        participantTypeId
      );
    },
    async handleAppointmentParticipantToggle(participant, isSelected) {
      if (!participant) return;

      if (participant.isOther) {
        await this.handleOtherAppointmentParticipantToggle(isSelected);
        return;
      }

      if (isSelected) {
        this.selectAppointmentParticipant(participant.id);
      } else {
        this.unselectAppointmentParticipant(participant.id);
      }
    },
    async handleOtherAppointmentParticipantToggle(isSelected) {
      if (isSelected) {
        this.selectAppointmentParticipant(
          OTHER_APPOINTMENT_PARTICIPANT_TYPE_ID
        );
        return;
      }

      if (this.appointmentOtherParticipants.length) {
        const confirm = await this.ShowCancelModal(
          'Confirmación',
          '¿Desea eliminar los participantes adicionales registrados?',
          'Confirmar',
          'Cancelar'
        );

        if (!confirm) {
          this.appointmentParticipantRenderKey++;
          return;
        }
      }

      this.unselectAppointmentParticipant(
        OTHER_APPOINTMENT_PARTICIPANT_TYPE_ID
      );
      this.appointmentOtherParticipantName = '';
      this.appointmentOtherParticipantError = '';
      this.appointmentOtherParticipants = [];
    },
    selectAppointmentParticipant(participantTypeId) {
      if (
        !this.selectedAppointmentParticipantTypeIds.includes(participantTypeId)
      ) {
        this.selectedAppointmentParticipantTypeIds.push(participantTypeId);
      }
    },
    unselectAppointmentParticipant(participantTypeId) {
      this.selectedAppointmentParticipantTypeIds = this.selectedAppointmentParticipantTypeIds.filter(
        (id) => id !== participantTypeId
      );
    },
    addAppointmentOtherParticipant() {
      if (!this.canAddAppointmentOtherParticipants) {
        this.appointmentOtherParticipantError = `Solo puede añadir hasta ${_MAX_OTHER_APPOINTMENT_PARTICIPANTS} participantes adicionales.`;
        return;
      }

      const participantName = this.appointmentOtherParticipantName.trim();

      if (!participantName) {
        this.appointmentOtherParticipantError =
          'Debe ingresar el nombre del participante antes de añadir.';
        return;
      }

      if (participantName.length > 100) {
        this.appointmentOtherParticipantError =
          'El nombre no debe exceder 100 caracteres.';
        return;
      }

      this.appointmentOtherParticipants.push(participantName);
      this.appointmentOtherParticipantName = '';
      this.appointmentOtherParticipantError = '';
      this.selectAppointmentParticipant(OTHER_APPOINTMENT_PARTICIPANT_TYPE_ID);
    },
    removeAppointmentOtherParticipant(index) {
      this.appointmentOtherParticipants.splice(index, 1);

      if (!this.appointmentOtherParticipants.length) {
        this.unselectAppointmentParticipant(
          OTHER_APPOINTMENT_PARTICIPANT_TYPE_ID
        );
        this.appointmentOtherParticipantName = '';
        this.appointmentOtherParticipantError = '';
      }
    },
    getStudentResultValue(result) {
      if (!result) return '';

      return `${result.fullName} - ${result.studentSieId}`;
    },
    handleStudentSearchChange(value) {
      this.studentSearch = value;
      this.studentSearchError = '';
      this.studentSearchResults = [];

      if (
        this.selectedStudent &&
        value !== this.getStudentResultValue(this.selectedStudent)
      ) {
        this.clearStudentSelection();
        return;
      }

      if (!value && !this.selectedStudent) {
        this.resetAppointmentForm();
      }
    },
    handleStudentSubmit(student) {
      if (!student) return;

      if (student.hasReachedAppointmentLimit) {
        this.clearStudentSelection();
        this.ShowToast(
          'Alerta',
          'El estudiante alcanzó límite de citas.',
          'error'
        );
        return;
      }

      this.selectedStudent = student;
      this.studentSearch = this.getStudentResultValue(student);
      this.studentSearchResults = [];
      this.model.studentSieId = student.studentSieId;
      this.model.citationNumber = null;
      this.studentSearchError = '';
      this.clearPurposes();
      this.clearAppointmentParticipants();
      this.fetchPendingEvaluations(student.studentSieId);
    },
    async searchStudents() {
      const input = this.studentSearch.trim();

      this.studentSearchError = '';
      this.studentSearchResults = [];

      if (this.isStudentSearchLoading) {
        return;
      }

      if (input.length < 3) {
        this.studentSearchError = 'Escriba al menos 3 caracteres para buscar.';
        return;
      }

      if (
        this.selectedStudent &&
        input === this.getStudentResultValue(this.selectedStudent)
      ) {
        return;
      }

      this.isStudentSearchLoading = true;

      try {
        const { data: response } = await compuAppointmentService.searchStudents(
          input
        );
        const students = Array.isArray(response) ? response : [];

        if (input !== this.studentSearch.trim()) {
          return;
        }

        this.studentSearchResults = students;
        this.studentSearchError = students.length
          ? ''
          : 'No se encontraron estudiantes.';
      } catch (error) {
        if (input !== this.studentSearch.trim()) {
          return;
        }

        const message = error?.response?.data;

        this.studentSearchError =
          typeof message === 'string' && message
            ? message
            : 'No se encontraron estudiantes.';
      } finally {
        this.isStudentSearchLoading = false;
      }
    },
    clearStudentSelection() {
      this.selectedStudent = null;
      this.appointmentHistory = [];
      this.isAppointmentHistoryLoading = false;
      this.resetAppointmentForm();
      this.studentSearch = '';
      this.studentSearchError = '';
      this.studentSearchResults = [];
    },
    async fetchStudentAppointmentHistory(studentSieId) {
      this.isAppointmentHistoryLoading = true;

      try {
        const {
          data: response,
        } = await compuAppointmentService.fetchStudentHistory(studentSieId);

        if (this.selectedStudent?.studentSieId === studentSieId) {
          this.appointmentHistory = Array.isArray(response) ? response : [];
        }
      } catch {
        if (this.selectedStudent?.studentSieId === studentSieId) {
          this.appointmentHistory = [];
        }
      } finally {
        if (this.selectedStudent?.studentSieId === studentSieId) {
          this.isAppointmentHistoryLoading = false;
        }
      }
    },
    getCitationLabel(citationNumber) {
      const labels = {
        1: '1ra Cita',
        2: '2da Cita',
        3: '3ra Cita',
        4: '4ta Cita',
        5: '5ta Cita',
      };

      return labels[citationNumber] || `${citationNumber || '-'} Cita`;
    },
    formatHistoryDate(date) {
      return this.formatDateWithCapitalizedMonth(date);
    },
    formatHistoryTime(time) {
      if (!time) return '-';

      const [hourText, minuteText] = String(time).split(':');
      const hours = Number(hourText);
      const minutes = Number(minuteText);

      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return '-';
      }

      const period = hours >= 12 ? 'PM' : 'AM';
      const hour = hours % 12 || 12;

      return `${hour}:${String(minutes).padStart(2, '0')}${period}`;
    },
    resetAppointmentForm() {
      this.clearPurposes();
      this.clearAppointmentParticipants();
      this.clearSelectedEvaluations();
      this.pendingEvaluations = [];
      this.model = {
        ...getDefaultModel(),
        ...this.getLoggedUserOfficialInfo(),
      };
      this.purposeOtherError = '';
      this.mechanismError = '';
      this.$nextTick(() => {
        this.$refs.formvalid?.reset?.();
      });
    },
    async addPurpose() {
      if (!this.model.purpose) return;

      if (this.isOtherPurposeSelected && !this.model.otherPurpose.trim()) {
        this.purposeOtherError = 'Campo requerido';
        return;
      }

      const alreadySelected = this.selectedPurposes.some(
        (purpose) => purpose.id === this.model.purpose.id
      );

      if (!alreadySelected) {
        const selectedPurpose = this.buildSelectedPurpose();
        this.selectedPurposes.push(selectedPurpose);
        await this.validateActivePurposeReminder();
      }

      this.model.purpose = null;
      this.model.otherPurpose = '';
      this.purposeOtherError = '';
    },
    addPendingEvaluation() {
      if (!this.selectedPendingEvaluation) return;

      const alreadySelected = this.selectedEvaluations.some(
        (evaluation) =>
          evaluation.studentEvaluationId ===
          this.selectedPendingEvaluation.studentEvaluationId
      );

      if (!alreadySelected) {
        this.selectedEvaluations.push(this.selectedPendingEvaluation);
      }

      this.selectedPendingEvaluation = null;
    },
    removePendingEvaluation(evaluationId) {
      this.selectedEvaluations = this.selectedEvaluations.filter(
        (evaluation) => evaluation.studentEvaluationId !== evaluationId
      );
    },
    clearSelectedEvaluations() {
      this.selectedPendingEvaluation = null;
      this.selectedEvaluations = [];
    },
    formatEvaluationDate(date) {
      return this.formatDateWithCapitalizedMonth(date);
    },
    formatDateWithCapitalizedMonth(date) {
      if (!date) return '-';

      const formattedDate = moment(date)
        .locale('es')
        .format('DD/MMM/YYYY');

      return formattedDate.replace(
        /\/(.)/,
        (match, letter) => `/${letter.toUpperCase()}`
      );
    },
    buildSelectedPurpose() {
      if (!this.isOtherPurposeSelected) {
        return this.model.purpose;
      }

      const otherPurpose = this.model.otherPurpose.trim();

      return {
        ...this.model.purpose,
        name: `${this.model.purpose.name}: ${otherPurpose}`,
        otherPurpose,
      };
    },
    closePanel() {
      this.$emit('input', false);
      this.$emit('close');
    },
    getCitationNumberValue(citationNumber) {
      if (citationNumber == null) {
        return null;
      }

      if (typeof citationNumber === 'object') {
        return citationNumber.id ?? null;
      }

      if (typeof citationNumber === 'string') {
        const trimmedCitationNumber = citationNumber.trim();

        if (trimmedCitationNumber.startsWith('{')) {
          try {
            const parsedCitationNumber = JSON.parse(trimmedCitationNumber);
            return parsedCitationNumber?.id ?? null;
          } catch {
            return null;
          }
        }
      }

      return citationNumber;
    },
    getCitationNumberOption(citationNumber) {
      const value = Number(this.getCitationNumberValue(citationNumber));

      if (!value) {
        return null;
      }

      return (
        this.citationNumberOptions.find(
          (option) => Number(option.id) === value
        ) || {
          id: value,
          name: `Cita ${value}`,
        }
      );
    },
    buildAppointmentParticipantsPayload() {
      const regularParticipants = this.selectedAppointmentParticipantTypeIds
        .filter((id) => id !== OTHER_APPOINTMENT_PARTICIPANT_TYPE_ID)
        .map((participantTypeId) => ({
          participantTypeId,
          otherDescription: null,
        }));

      const otherParticipants = this.appointmentOtherParticipants.map(
        (participant) => ({
          participantTypeId: OTHER_APPOINTMENT_PARTICIPANT_TYPE_ID,
          otherDescription: participant,
        })
      );

      return [...regularParticipants, ...otherParticipants];
    },
    buildPayload() {
      const { model, selectedPurposes } = this;
      const citationNumber = this.getCitationNumberValue(model.citationNumber);

      return {
        id: this.currentAppointmentId || 0,
        citationNumber,
        studentSieId: model.studentSieId,
        appointmentDate: model.date ? new Date(model.date).toISOString() : null,
        startTime: dateUtil.toTimeSpan(model.startTime, model.startAmPm),
        endTime: dateUtil.toTimeSpan(model.endTime, model.endAmPm),
        location: model.place,
        officialFirstName: model.firstName,
        officialInitial: model.initial,
        officialLastName: model.lastName,
        officialSecondLastName: model.secondLastName,
        officialEmail: model.email,
        officialPhone: model.phone,
        includeParents: model.parents,
        includeStudent: model.student,
        notifyPersonal: model.notifyPersonal,
        notifyPhone: model.notifyPhone,
        notifyEmail: model.notifyEmail,
        notifyOther: model.notifyOther,
        otherNotificationDescription: model.notifyOther
          ? model.otherNotification
          : null,
        isElectronicSignature: model.isElectronicSignature,
        purposes: selectedPurposes.map((p) => ({
          id: p.id,
          otherDescription: p.otherPurpose || null,
        })),
        participants: this.buildAppointmentParticipantsPayload(),
        evaluations: this.hasEvaluationDiscussionPurpose
          ? this.selectedEvaluations.map((evaluation) => ({
              id: evaluation.id,
              studentEvaluationId: evaluation.studentEvaluationId,
              name: evaluation.name,
              evaluationDate: evaluation.evaluationDate,
            }))
          : [],
      };
    },
    async saveCoordination({ navigateToHub = false, justSave = false } = {}) {
      if (this.hasActivePurposeReminder) {
        this.ShowToast(
          'Alerta',
          'Se recomienda dar seguimiento a la cita existente antes de crear una nueva.',
          'warning'
        );
        return;
      }

      try {
        this.isSaving = true;
        const payloadModel = this.buildPayload();
        payloadModel.citationNumber = this.getCitationNumberValue(
          payloadModel.citationNumber
        );
        const payload = objectToFormData(payloadModel, {
          indices: true,
        });
        payload.set('citationNumber', payloadModel.citationNumber);
        if (this.manuscriptFileInput) {
          payload.append('citationFile', this.manuscriptFileInput);
        }
        if (justSave) {
          payload.append('justSaveFlag', true);
        }
        const shouldCreateRecoordination =
          this.isRecoordination &&
          this.currentAppointmentId &&
          Number(this.currentAppointmentId) === Number(this.appointmentId);

        const { data: savedId } = shouldCreateRecoordination
          ? await compuAppointmentService.recoordinateAppointment(
              this.currentAppointmentId,
              payload
            )
          : await compuAppointmentService.saveAppointment(payload);
        const isNew = !this.currentAppointmentId;
        this.currentAppointmentId = savedId;
        if (justSave) {
          if (isNew) {
            this.model.citationNumber = this.getCitationNumberOption(
              payloadModel.citationNumber
            );
          }
          this.$emit('saved', savedId);
          return;
        }
        if (this.isRecoordination) {
          this.ShowToast('', 'Cita re-coordinada exitosamente.', 'success');
        } else if (this.hasSelectMechanisManuscript) {
          this.ShowToast('', 'Cita coordinada exitosamente.', 'success');
        } else if (isNew) {
          this.ShowToast('', 'Cita guardada exitosamente.', 'success');
        }
        this.$emit('saved', savedId);
        if (navigateToHub) {
          await this.fetchSignatureHubInfo(savedId);
          this.$emit('open-signature-hub', {
            appointmentId: savedId,
            keepAppointmentQuery: true,
          });
        } else {
          this.closePanel();
        }
      } catch (error) {
        const message =
          error?.response?.data || 'Ocurrió un error al guardar la cita.';
        this.ShowToast('Error', message, 'error');
      } finally {
        this.isSaving = false;
      }
    },
    async continueAppointment() {
      if (this.isSaving) {
        return;
      }
      if (this.hasActivePurposeReminder) {
        this.ShowToast(
          'Alerta',
          'Se recomienda dar seguimiento a la cita existente antes de crear una nueva.',
          'warning'
        );
        return;
      }

      if (!this.hasSelectedStudent) {
        this.studentSearchError = 'Favor seleccionar número de SIE';
        this.ShowToast(
          'Error',
          'Favor de completar la información solicitada.',
          'error'
        );
        return;
      }

      const isFormValid = await this.$refs.formvalid.validate();

      const isMechanismValid = this.model.isElectronicSignature !== null;
      if (!isMechanismValid) {
        this.mechanismError = 'Favor seleccionar el mecanismo.';
      }

      if (this.requiresManuscriptTemplateDownload) {
        this.ShowToast(
          'Alerta',
          'Debe descargar la citación antes de coordinar.',
          'error'
        );
        return;
      }

      if (!isFormValid || !isMechanismValid) {
        this.ShowToast(
          'Error',
          'Favor de completar la información solicitada.',
          'error'
        );
        return;
      }

      await this.saveCoordination({
        navigateToHub: this.hasSelectMechanismElectronic,
      });
    },
    async handleConfigureSignatures() {
      if (this.hasActivePurposeReminder) {
        this.ShowToast(
          'Alerta',
          'Se recomienda dar seguimiento a la cita existente antes de crear una nueva.',
          'warning'
        );
        return;
      }

      if (!this.hasSelectedStudent) {
        this.studentSearchError = 'Favor seleccionar número de SIE';
        this.ShowToast(
          'Error',
          'Favor de completar la información solicitada.',
          'error'
        );
        return;
      }

      const isFormValid = await this.$refs.formvalid.validate();

      if (!isFormValid) {
        this.ShowToast(
          'Error',
          'Favor de completar la información solicitada.',
          'error'
        );
        return;
      }

      await this.saveCoordination({ navigateToHub: true });
    },
    async removePurpose(item) {
      const id = item ? item.id : null;

      this.selectedPurposes = this.selectedPurposes.filter(
        (purpose) => purpose.id !== id
      );

      if (!this.hasEvaluationDiscussionPurpose) {
        this.clearSelectedEvaluations();
      }

      await this.validateActivePurposeReminder();
    },
    applyAppointmentParticipantsFromDto(participants) {
      const normalizedParticipants = Array.isArray(participants)
        ? participants
        : [];

      const selectedParticipantIds = normalizedParticipants
        .filter((participant) => !participant.isOther)
        .map((participant) => participant.participantTypeId);

      this.appointmentOtherParticipants = normalizedParticipants
        .filter((participant) => participant.isOther)
        .map((participant) => participant.otherDescription)
        .filter(Boolean);

      if (this.appointmentOtherParticipants.length) {
        selectedParticipantIds.push(OTHER_APPOINTMENT_PARTICIPANT_TYPE_ID);
      }

      this.selectedAppointmentParticipantTypeIds = [
        ...new Set(selectedParticipantIds),
      ];
      this.appointmentOtherParticipantName = '';
      this.appointmentOtherParticipantError = '';
    },
    getLoggedUserOfficialInfo() {
      const profile = this.userProfile || {};
      const fullName = getFirstAvailableValue(profile, ['fullName', 'name']);
      const nameParts = fullName.split(' ').filter(Boolean);

      return {
        firstName:
          getFirstAvailableValue(profile, [
            'firstName',
            'firstname',
            'givenName',
            'givenname',
          ]) ||
          nameParts[0] ||
          '',
        initial: getFirstAvailableValue(profile, ['initial', 'middleInitial']),
        lastName:
          getFirstAvailableValue(profile, [
            'lastName',
            'lastname',
            'lastPaternalName',
            'lastpaternalname',
          ]) || (nameParts.length > 1 ? nameParts[nameParts.length - 2] : ''),
        secondLastName:
          getFirstAvailableValue(profile, [
            'secondLastName',
            'secondlastname',
            'lastMaternalName',
            'lastmaternalname',
          ]) || (nameParts.length > 2 ? nameParts[nameParts.length - 1] : ''),
        email: getFirstAvailableValue(profile, ['email', 'mail']),
        phone: getFirstAvailableValue(profile, [
          'phone',
          'telephone',
          'telephoneNumber',
          'phonenumber',
        ]),
      };
    },
    resetPanel() {
      this.resetAppointmentForm();
      this.clearStudentSelection();
      this.signatureHubInfo = null;
      this.currentAppointmentId = null;
      this.appointmentStatusCode = null;
      this.hasDownloadedManuscriptTemplate = false;
      this.manuscriptFileInput = null;
      this.manuscriptFileInputKey += 1;
      this.manuscriptTemplateSnapshot = null;
      this.manuscriptDocumentUploaded = false;
      this.manuscriptDocument = null;
    },
    async loadAppointment(id) {
      try {
        const { data: dto } = await compuAppointmentService.getAppointmentInfo(
          id
        );
        this.populateFromDto(dto);
        if (!this.isRecoordination && dto.isElectronicSignature) {
          await this.fetchSignatureHubInfo(id);
        }
      } catch {
        this.ShowToast(
          'Error',
          'No se pudo cargar la información de la cita.',
          'error'
        );
      }
    },
    async fetchSignatureHubInfo(appointmentId) {
      const response = await _getSignatureHubInfo(
        SignatureHubTypes.COMPU_APPOINTMENT,
        appointmentId
      );
      if (response?.data) {
        this.signatureHubInfo = response.data;
      }
    },
    populateFromDto(dto) {
      const start = dateUtil.fromTimeSpan(dto.startTime);
      const end = dateUtil.fromTimeSpan(dto.endTime);

      this.currentAppointmentId = dto.id;
      this.appointmentStatusCode = dto.statusId ?? null;

      this.model = {
        ...getDefaultModel(),
        studentSieId: dto.studentSieId,
        parents: dto.includeParents,
        student: dto.includeStudent,
        date:
          !this.isRecoordination && dto.appointmentDate
            ? new Date(dto.appointmentDate)
            : null,
        startTime: this.isRecoordination ? null : start.time,
        startAmPm: this.isRecoordination ? 'AM' : start.period,
        endTime: this.isRecoordination ? null : end.time,
        endAmPm: this.isRecoordination ? 'AM' : end.period,
        place: dto.location,
        firstName: dto.officialFirstName,
        initial: dto.officialInitial || '',
        lastName: dto.officialLastName,
        secondLastName: dto.officialSecondLastName,
        email: dto.officialEmail,
        phone: dto.officialPhone,
        notifyPersonal: dto.notifyPersonal,
        notifyPhone: dto.notifyPhone,
        notifyEmail: dto.notifyEmail,
        notifyOther: dto.notifyOther,
        otherNotification: dto.otherNotificationDescription || '',
        isElectronicSignature: this.isRecoordination
          ? null
          : dto.isElectronicSignature,
        citationNumber: this.getCitationNumberOption(dto.citationNumber),
        digitalSignatureDocumentCancellationReason: this.isRecoordination
          ? null
          : dto.digitalSignatureDocumentCancellationReason || null,
      };

      this.selectedPurposes = (dto.purposes || []).map((p) => ({
        id: p.id,
        name: p.otherDescription ? `${p.name}: ${p.otherDescription}` : p.name,
        otherPurpose: p.otherDescription || null,
      }));

      this.selectedEvaluations = (dto.evaluations || []).map((evaluation) => ({
        id: evaluation.id,
        studentEvaluationId: evaluation.studentEvaluationId,
        name: evaluation.name,
        evaluationDate: evaluation.evaluationDate,
      }));

      this.applyAppointmentParticipantsFromDto(dto.participants || []);

      this.selectedStudent = {
        studentSieId: dto.studentSieId,
        fullName: dto.studentFullName,
        appointmentCount: dto.citationNumber ?? 0,
        lastCitationNumber: dto.citationNumber ?? null,
        nextCitationNumber: dto.citationNumber ?? null,
        requiresCitationNumberSelection: false,
        availableCitationNumbers: [],
        hasReachedAppointmentLimit: false,
      };
      this.studentSearch = this.getStudentResultValue(this.selectedStudent);
      const isResumingManuscript =
        !this.isRecoordination &&
        dto.isElectronicSignature === false &&
        Number(this.appointmentStatusCode) ===
          compuAppointmentStatus.PendienteDeFirma;
      if (isResumingManuscript) {
        // Resume the persisted citation; edits still invalidate its template snapshot.
        this.hasDownloadedManuscriptTemplate = true;
        this.manuscriptTemplateSnapshot = this.getManuscriptTemplateSnapshot();
      }
      this.fetchPurposeOptions({ preserveSelection: isResumingManuscript });
      this.fetchAppointmentParticipantOptions();
      this.fetchPendingEvaluations(dto.studentSieId);
    },
    handleSelectMechanism(selected) {
      const value = selected?.value;
      this.model.isElectronicSignature = value ?? null;
      this.mechanismError = '';

      if (value !== true) {
        this.signatureHubInfo = null;
      }
    },
    async handleSignDocument() {
      try {
        const {
          data: url,
        } = await compuAppointmentService.getCompuMeetingCoordinationUrl(
          this.currentAppointmentId
        );
        if (url) {
          window.open(url, '_blank');
        }
      } catch (err) {
        this.ShowToast(
          'Alerta',
          err?.response?.data?.message ||
            'No fue posible obtener el enlace de firma.',
          'error'
        );
      }
    },
    async downloadManuscriptTemplate() {
      const shouldSaveBeforeDownload =
        !this.currentAppointmentId ||
        !this.hasDownloadedManuscriptTemplate ||
        !this.manuscriptTemplateSnapshot;

      if (shouldSaveBeforeDownload) {
        if (!this.hasSelectedStudent) {
          this.studentSearchError = 'Favor seleccionar número de SIE';
          this.ShowToast(
            'Error',
            'Favor de completar la información solicitada.',
            'error'
          );
          return;
        }
        const isFormValid = await this.$refs.formvalid.validate();
        const isMechanismValid = this.model.isElectronicSignature !== null;
        if (!isMechanismValid) {
          this.mechanismError = 'Favor seleccionar el mecanismo.';
        }
        if (!isFormValid || !isMechanismValid) {
          this.ShowToast(
            'Error',
            'Favor de completar la información solicitada.',
            'error'
          );
          return;
        }
        await this.saveCoordination({ justSave: true });
        if (!this.currentAppointmentId) return;
      }
      try {
        const citationNumber = this.getCitationNumberValue(
          this.model.citationNumber
        );

        await reportService.getCompuMeetingCoordination(
          this.model.studentSieId,
          citationNumber
        );
        this.hasDownloadedManuscriptTemplate = true;
        this.manuscriptTemplateSnapshot = this.getManuscriptTemplateSnapshot();
      } catch (err) {
        this.ShowToast(
          'Alerta',
          err?.response?.data?.message ||
            'No fue posible descargar la plantilla.',
          'error'
        );
      }
    },
    downloadManuscriptDocument() {},
    onRemoveManuscript() {
      this.manuscriptDocumentUploaded = false;
      this.manuscriptFileInput = null;
      this.manuscriptDocument = null;
      this.hasDownloadedManuscriptTemplate = true;
      this.manuscriptFileInputKey += 1;
    },
    onManuscriptFileSelected(file) {
      this.manuscriptFileInput = file || null;
    },
    getManuscriptTemplateSnapshot() {
      const normalizeDate = (date) => {
        if (!date) return null;
        const parsedDate = date instanceof Date ? date : new Date(date);

        if (Number.isNaN(parsedDate.getTime())) {
          return date;
        }

        return parsedDate.toISOString().substring(0, 10);
      };

      return JSON.stringify({
        studentSieId: this.model.studentSieId || null,
        citationNumber: this.getCitationNumberValue(this.model.citationNumber),
        date: normalizeDate(this.model.date),
        startTime: this.model.startTime || null,
        startAmPm: this.model.startAmPm || null,
        endTime: this.model.endTime || null,
        endAmPm: this.model.endAmPm || null,
        place: this.model.place || '',
        parents: Boolean(this.model.parents),
        student: Boolean(this.model.student),
        firstName: this.model.firstName || '',
        initial: this.model.initial || '',
        lastName: this.model.lastName || '',
        secondLastName: this.model.secondLastName || '',
        email: this.model.email || '',
        phone: this.model.phone || '',
        notifyPersonal: Boolean(this.model.notifyPersonal),
        notifyPhone: Boolean(this.model.notifyPhone),
        notifyEmail: Boolean(this.model.notifyEmail),
        notifyOther: Boolean(this.model.notifyOther),
        otherNotification: this.model.notifyOther
          ? this.model.otherNotification || ''
          : '',
        isElectronicSignature: this.model.isElectronicSignature,
        purposes: this.selectedPurposes.map((purpose) => ({
          id: purpose.id,
          otherPurpose: purpose.otherPurpose || '',
        })),
        participants: this.buildAppointmentParticipantsPayload(),
        evaluations: this.hasEvaluationDiscussionPurpose
          ? this.selectedEvaluations.map((evaluation) => ({
              id: evaluation.id || null,
              studentEvaluationId: evaluation.studentEvaluationId,
            }))
          : [],
      });
    },
    invalidateManuscriptDocumentIfOutdated(currentSnapshot) {
      if (
        !this.manuscriptTemplateSnapshot ||
        currentSnapshot === this.manuscriptTemplateSnapshot
      ) {
        return;
      }

      this.hasDownloadedManuscriptTemplate = false;
      this.manuscriptFileInput = null;
      this.manuscriptDocumentUploaded = false;
      this.manuscriptDocument = null;
      this.manuscriptTemplateSnapshot = null;
      this.manuscriptFileInputKey += 1;
    },
  },
};
</script>

<style scoped lang="scss">
@use "sass:map";
@import '@/assets/scss/variables/_colors.scss';

.coordinate-panel-shell {
  position: fixed;
  inset: 0;
  z-index: 1040;
}

.coordinate-panel-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 30, 32, 0.42);
  backdrop-filter: blur(2px);
}

.coordinate-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(650px, calc(100vw - 40px));
  display: flex;
  flex-direction: column;
  background: map.get($colors, 'white');
  box-shadow: 0 18px 50px rgba(20, 35, 45, 0.22);
  overflow: hidden;
}

.coordinate-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 8px;
}

.coordinate-panel__title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: map.get($colors, 'gray-7');
  font-weight: 600;
}

.coordinate-panel__icon,
.coordinate-panel__section-icon {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: map.get($colors, 'orange');
  background: map.get($colors, 'white');
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.14);
}

.coordinate-panel__header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.coordinate-panel__icon-button {
  border: 0;
  background: transparent;
  color: map.get($colors, 'gray-6');
  padding: 4px;
  line-height: 1;
}

.coordinate-panel__icon-button--danger {
  color: map.get($colors, 'red');
}

.coordinate-panel__body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 24px;
}

.coordinate-panel__tabs {
  --tab-active-color: #{map.get($colors, 'orange')};
  --tab-font-size: 16px;
  width: 100%;
}

.coordinate-panel__tabs ::v-deep .tab-wrapper {
  width: 100%;
}

.coordinate-panel__tabs ::v-deep .coordinate-panel__tabs-nav {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 0 0 16px;
}

.coordinate-panel__tabs ::v-deep .coordinate-panel__tabs-nav .nav-link {
  width: 100%;
  padding: 12px;
  text-align: center;
}

.coordinate-panel__tabs ::v-deep .custom-tab-container .nav-link.active,
.coordinate-panel__tabs ::v-deep .coordinate-panel__tabs-nav .nav-link.active {
  border-bottom-color: map.get($colors, 'orange') !important;
  color: map.get($colors, 'orange') !important;
}

.coordinate-panel__summary {
  display: flex;
  justify-content: space-between;
  background: map.get($colors, 'secondary-3');
  border-radius: 4px;
  padding: 12px 14px;
  margin-bottom: 14px;
}

.coordinate-panel__student-search {
  position: relative;
  margin-bottom: 14px;
}

.coordinate-panel__student-search-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 126px;
  gap: 12px;
  align-items: end;
}

.coordinate-panel__student-input-container {
  position: relative;
  min-width: 0;
}

.coordinate-panel__student-clear {
  position: absolute;
  right: 10px;
  bottom: 7px;
  width: 22px;
  height: 22px;
  padding: 0;
  color: map.get($colors, 'gray-6');
  font-size: 20px;
  line-height: 20px;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.coordinate-panel__student-search-button {
  width: 100%;
}

.coordinate-panel__student-results {
  position: absolute;
  z-index: 10;
  top: 68px;
  right: 138px;
  left: 0;
  max-height: 224px;
  overflow-y: auto;
  background: map.get($colors, 'white');
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
}

.coordinate-panel__student-result {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 9px 12px;
  text-align: left;
  background: map.get($colors, 'white');
  border: 1px solid map.get($colors, 'gray-9');
  cursor: pointer;
}

.coordinate-panel__student-result:hover,
.coordinate-panel__student-result:focus {
  background: map.get($colors, 'secondary-3');
  outline: none;
}

.coordinate-panel__student-result-name {
  color: map.get($colors, 'black');
  font-size: 14px;
  font-weight: 600;
}

.coordinate-panel__student-result-sie {
  color: map.get($colors, 'gray-6');
  font-size: 14px;
  font-style: italic;
}

.coordinate-panel__form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.coordinate-panel__fieldset {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0;
  margin: 0;
  border: 0;
}

.coordinate-panel__fieldset:disabled {
  opacity: 0.65;
  pointer-events: none;
}

.coordinate-panel__form ::v-deep .width-container {
  width: 100% !important;
}

.coordinate-panel__form ::v-deep .custom-datetime-picker-container {
  position: relative;
}

.coordinate-panel__form ::v-deep .custom-datetime-picker-container > i {
  left: 0;
  top: 0;
}

.coordinate-panel__form
  ::v-deep
  .custom-datetime-picker-container
  .vdp-datepicker__clear-button
  .input-group-text {
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
}

.coordinate-panel__form
  ::v-deep
  .custom-datetime-picker-container
  .vdp-datepicker__clear-button
  i.prevent-close-dialog {
  position: static;
  display: block;
  margin: 0;
  line-height: 1;
}

.coordinate-panel__grid {
  display: grid;
  gap: 12px;
}

.coordinate-panel__grid--two {
  grid-template-columns: 1fr 1fr;
}

.coordinate-panel__grid--name {
  grid-template-columns: minmax(0, 1fr) 80px;
}

.coordinate-panel__label {
  display: block;
  color: map.get($colors, 'gray-6');
  font-size: 14px;
  margin-bottom: 6px;
}

.coordinate-panel__purpose-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: end;
}

.coordinate-panel__add-button {
  margin-bottom: 1px;
}

.coordinate-panel__selected {
  border-bottom: 1px solid map.get($colors, 'gray-3');
  padding-bottom: 12px;
}

.coordinate-panel__selected ::v-deep .chips-container p {
  font-weight: 400 !important;
  font-size: 14px;
}

.coordinate-panel__selected--readonly {
  padding-bottom: 6px;
}

.coordinate-panel__selected--readonly ::v-deep .mt-3 {
  margin-top: 0 !important;
}

.coordinate-panel__selected--readonly ::v-deep .chips-container {
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.coordinate-panel__selected--readonly ::v-deep .chips-container p {
  display: none;
}

.coordinate-panel__selected--readonly ::v-deep .label {
  margin-top: 2px;
}

.coordinate-panel__evaluations {
  border-bottom: 1px solid map.get($colors, 'gray-3');
  padding-bottom: 12px;
}

.coordinate-panel__evaluation-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: end;
}

.coordinate-panel__evaluation-select {
  background-color: white;
}

.coordinate-panel__evaluation-select::v-deep .vs__dropdown-toggle {
  min-height: 38px;
}

.coordinate-panel__evaluation-select::v-deep .vs__dropdown-option {
  white-space: normal;
}

.coordinate-panel__evaluation-select::v-deep
  .vs__dropdown-menu
  .vs__dropdown-option--highlight {
  background: #f4f6f3 !important;
  color: map.get($colors, 'secondary-6') !important;
}

.coordinate-panel__evaluation-option {
  display: flex;
  flex-direction: column;
  gap: 3px;
  line-height: 1.25;
  padding: 4px 0;
}

.coordinate-panel__evaluation-option-name {
  color: map.get($colors, 'secondary-6');
  font-weight: 400;
}

.coordinate-panel__evaluation-option-date {
  color: map.get($colors, 'gray-6');
  font-size: 13px;
}

.coordinate-panel__evaluation-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.coordinate-panel__evaluation-row-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  border-radius: 4px;
  min-height: 42px;
  padding: 10px 12px;
  background: map.get($colors, 'secondary-3');
}

.coordinate-panel__evaluation-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.coordinate-panel__empty-message {
  color: map.get($colors, 'gray-6');
  font-size: 14px;
  margin-top: 8px;
}

.coordinate-panel__notification-other {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 8px;
  align-items: start;
}

.coordinate-panel__notification-other-input {
  min-width: 0;
}

.coordinate-panel__participants {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.coordinate-panel__participant-other {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr) auto;
  gap: 8px;
  align-items: start;
}

.coordinate-panel__participant-other-input {
  min-width: 0;
}

.coordinate-panel__participant-other-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
}

.coordinate-panel__participant-other-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 4px;
  min-height: 42px;
  padding: 10px 12px;
  background: map.get($colors, 'secondary-3');
}

.coordinate-panel__participant-remove {
  border: 0;
  background: transparent;
  color: map.get($colors, 'red');
  cursor: pointer;
}

.coordinate-panel ::v-deep .input-group .option-input:checked + label span {
  background-color: #2db782 !important;
  border-color: #2db782;
}

.coordinate-panel
  ::v-deep
  .input-group
  .option-input:checked
  + label
  span
  .ic-check {
  filter: brightness(0) invert(1);
}

.coordinate-panel__section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 6px;
}

.coordinate-panel__empty-history {
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: map.get($colors, 'gray-6');
  font-weight: 600;
}

.coordinate-panel__empty-history i {
  color: map.get($colors, 'orange');
  font-size: 28px;
}

.coordinate-panel__history-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.coordinate-panel__history-card {
  background: map.get($colors, 'secondary-3');
  border-radius: 4px;
  padding: 16px 18px 18px;
}

.coordinate-panel__history-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.coordinate-panel__history-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 16px;
  color: map.get($colors, 'white');
  background: map.get($colors, 'orange');
  font-weight: 600;
  font-size: 14px;
}

.coordinate-panel__history-action {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: map.get($colors, 'white');
  background: map.get($colors, 'gray-2');
}

.coordinate-panel__history-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 24px;
}

.coordinate-panel__history-label,
.coordinate-panel__history-value {
  display: block;
}

.coordinate-panel__history-label {
  color: map.get($colors, 'gray-6');
  font-size: 14px;
  margin-bottom: 2px;
}

.coordinate-panel__history-value {
  color: map.get($colors, 'black');
  font-weight: 500;
  overflow-wrap: anywhere;
}

.coordinate-panel-enter-active,
.coordinate-panel-leave-active {
  transition: opacity 0.18s ease;
}

.coordinate-panel-enter,
.coordinate-panel-leave-to {
  opacity: 0;
}

.coordinate-panel-enter .coordinate-panel,
.coordinate-panel-leave-to .coordinate-panel {
  transform: translateX(28px);
}

.coordinate-panel-enter-active .coordinate-panel,
.coordinate-panel-leave-active .coordinate-panel {
  transition: transform 0.18s ease;
}

@media (max-width: 576px) {
  .coordinate-panel {
    inset: 0;
    width: 100vw;
    border-radius: 0;
  }

  .coordinate-panel__grid--two,
  .coordinate-panel__grid--name,
  .coordinate-panel__purpose-row,
  .coordinate-panel__history-grid {
    grid-template-columns: 1fr;
  }

  .coordinate-panel__student-search-row {
    grid-template-columns: minmax(0, 1fr) 110px;
  }

  .coordinate-panel__student-results {
    right: 122px;
  }
}

.coordinate-panel__sign-plan--disabled {
  opacity: 0.65;
  pointer-events: none;
}

.coordinate-panel__sign-plan ::v-deep .d-flex.flex-md-row {
  flex-direction: column !important;
  align-items: flex-start !important;
  border-bottom: none !important;
}

.coordinate-panel__sign-plan ::v-deep .custom-radio__extended {
  width: 100%;
  margin-top: 16px;
  margin-bottom: 0;
}

.coordinate-panel__sign-plan ::v-deep .custom-radio-container {
  width: 100%;
  gap: 8px;
}

.coordinate-panel__sign-plan ::v-deep .custom-radio-container > div {
  flex: 1;
}

.coordinate-panel__sign-plan
  ::v-deep
  .custom-radio-container
  .custom-radio-button {
  width: 100% !important;
}

.coordinate-panel__section--disabled {
  opacity: 0.65;
  pointer-events: none;
}

.coordinate-panel__manuscript-section {
  border-top: 1px solid map.get($colors, 'gray-3');
  padding-top: 12px;
}

.coordinate-panel__manuscript-file-input ::v-deep .custom-file-input-wrapper {
  margin: 0;
}
</style>
